/**
 * IconPoints.tsx — a short list where every line leads with an icon in a soft
 * tinted disc: a bold headline, and at most one short line under it.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: journey copy pass
 * (Ashley Haveloch-Jones, via the CTO, 2026-09-26).
 *
 * WHY. Ashley's review of the journey on a phone: "too much text", "way less
 * words per section", "reduce the need to read", and the CTO's "use icons more
 * where ever possible". The journey's entry loader card already says its steps
 * this way (`ui/LoaderJourneyCard.tsx`); this is the same look for a STATIC
 * screen — "What happens next", the card-hold box at checkout, the weight
 * declaration and (since the checkout dedupe, 2026-09-26) the plan screen's
 * "What you're agreeing to" — so they read as one family instead of paragraphs.
 *
 * ⚠️ THE ICON IS DECORATION, NEVER THE MEANING. Each disc is `aria-hidden` and
 * every point reads the same with its icon removed; the headline carries the
 * claim. Pass `ordered` when the points are a SEQUENCE (the steps of the
 * journey) and leave it off when they are not (the facts about a card hold) —
 * the list type is what a screen reader announces, so it must be true.
 *
 * Inline styles, like the rest of the journey's small components, so it renders
 * the same inside and outside `.gps-journey-root` (checkout mounts it too).
 */
import type { CSSProperties, ReactElement, ReactNode } from 'react';

export interface IconPoint {
  /** Stable React key — never shown. */
  key: string;
  /** A decorative glyph from `ui/icons.tsx`, drawn at 20px. */
  icon: ReactElement;
  title: ReactNode;
  /** One short line. Omit rather than pad. */
  body?: ReactNode;
}

const listStyle: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'grid',
  gap: '14px',
};

const itemStyle: CSSProperties = { display: 'flex', gap: '12px', alignItems: 'flex-start' };

const discStyle: CSSProperties = {
  flex: 'none',
  width: '36px',
  height: '36px',
  borderRadius: 'var(--gps-r-pill)',
  background: 'var(--gps-primary-light)',
  color: 'var(--gps-green-text)',
  display: 'grid',
  placeItems: 'center',
};

const baseTitleStyle: CSSProperties = { display: 'block', fontSize: '15.5px', fontWeight: 600, lineHeight: 1.35 };

const bodyStyle: CSSProperties = {
  display: 'block',
  fontSize: '14px',
  lineHeight: 1.45,
  color: 'var(--gps-muted)',
  marginTop: '2px',
};

/**
 * `compact`: a quieter list for statements the patient READS rather than scans
 * — the weight declaration's acknowledgements, which are clinician wording
 * shown verbatim. The same icon-led rhythm, a smaller disc, and the text in the
 * body face at body weight (a paragraph set bold is harder to read, not easier).
 */
const compactDiscStyle: CSSProperties = { ...discStyle, width: '30px', height: '30px' };
const compactTitleStyle: CSSProperties = {
  display: 'block',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: 1.45,
  color: 'var(--gps-jux-body, var(--gps-muted))',
};

export function IconPoints({
  points,
  ordered = false,
  compact = false,
  style,
  titleStyle,
}: {
  points: ReadonlyArray<IconPoint>;
  ordered?: boolean;
  compact?: boolean;
  style?: CSSProperties;
  /**
   * LOOK ONLY, merged over the headline's style (2026-09-26). The plan screen's
   * "What you're agreeing to" is `compact` — one sentence a point with its bold
   * lead running into it — but it is the contract a patient ticks, so it keeps
   * full body ink (`--gps-text`) where the q14 acknowledgements take the quieter
   * help ink: greying a term of the agreement would be the quiet way of not
   * saying it (the rule `termEndNote` in PlanSelection set first). Absent →
   * exactly the style above.
   */
  titleStyle?: CSSProperties;
}) {
  const List = ordered ? 'ol' : 'ul';
  return (
    <List className="gps-icon-points" data-compact={compact ? 'true' : undefined} style={{ ...listStyle, ...(compact ? { gap: '10px' } : null), ...style }}>
      {points.map((point) => (
        <li key={point.key} style={itemStyle}>
          <span aria-hidden="true" className="gps-icon-points-disc" style={compact ? compactDiscStyle : discStyle}>
            {point.icon}
          </span>
          {/* Padded to the disc's centre line, so a one-line headline sits level with its icon. */}
          <span style={{ minWidth: 0, paddingTop: compact ? '5px' : '7px' }}>
            <span style={{ ...(compact ? compactTitleStyle : baseTitleStyle), ...titleStyle }}>{point.title}</span>
            {/* A real space between the two blocks: invisible, but the point's
                text (and a screen reader's reading of it) is "Title. Body",
                never "Title.Body". */}
            {point.body ? (
              <>
                {' '}
                <span style={bodyStyle}>{point.body}</span>
              </>
            ) : null}
          </span>
        </li>
      ))}
    </List>
  );
}
